import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";

import {
  Box,
  Grid,
  Card,
  CardHeader,
  CardContent,
  Typography,
  Stack,
  Button,
  Divider,
  Chip,
  IconButton,
  Snackbar,
  Alert,
} from "@mui/material";

import { alpha } from "@mui/material/styles";
import axios from "axios";
import config from "../../../config";

import MoreVertIcon from "@mui/icons-material/MoreVert";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { useNavigate } from "react-router-dom";

/* -------------------- HELPERS -------------------- */

const makeText = (theme) => {
  const white = theme.palette.common.white;
  return {
    primary: white,
    secondary: alpha(white, 0.72),
    disabled: alpha(white, 0.5),
    subtle: alpha(white, 0.35),
  };
};

function KPIStatCard({ label, value, hint, children }) {
  return (
    <Card>
      <CardContent>
        <Stack spacing={0.75}>
          <Typography variant="body2" sx={(theme) => ({ color: makeText(theme).secondary })}>
            {label}
          </Typography>

          {children ? (
            children
          ) : (
            <>
              <Typography variant="h4" sx={(theme) => ({ color: makeText(theme).primary })}>
                {value}
              </Typography>

              <Typography variant="caption" sx={(theme) => ({ color: makeText(theme).secondary })}>
                {hint}
              </Typography>
            </>
          )}
        </Stack>
      </CardContent>
    </Card>
  );
}

function CardHeaderWhite({ title, subheader, action }) {
  return (
    <CardHeader
      title={title}
      subheader={subheader}
      action={action}
      sx={(theme) => {
        const t = makeText(theme);
        return {
          "& .MuiCardHeader-title": { color: t.primary },
          "& .MuiCardHeader-subheader": { color: t.secondary },
        };
      }}
    />
  );
}

function getStatusColor(status) {
  switch (status) {
    case "new":
      return "info";
    case "contacted":
      return "warning";
    case "closed":
      return "success";
    default:
      return "default";
  }
}

function getStatusLabel(status, t) {
  switch (status) {
    case "new":
      return t("status.new");
    case "contacted":
      return t("status.contacted");
    case "closed":
      return t("status.closed");
    default:
      return "-";
  }
}

function formatDate(date) {
  if (!date) return "-";
  return new Date(date).toLocaleString("el-GR");
}

const dashboardTableColumns = "1.5fr 1.4fr 1fr 1.2fr";

const cellSx = {
  minWidth: 0,
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
  color: "#fff",
  fontSize: "1rem",
};

/* -------------------- PAGE -------------------- */

export default function DashboardAffiliatePage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const token = useMemo(() => localStorage.getItem("admin_jwt"), []);
  const [dashboardStats, setDashboardStats] = useState({
    newInterestsCount: 0,
    totalInterestsCount: 0,
    savesCount: 0,
    latest5: [],
  });
  const [feedCount, setFeedCount] = useState(0);

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const [statsRes, feedRes] = await Promise.all([
          axios.get(`${config.BACKEND_BASE_URL}/api/admin/dashboard/stats`, {
            headers: { Authorization: `Bearer ${token}` },
          }),
          axios.get(`${config.BACKEND_BASE_URL}/api/admin/event-requests/feed`, {
            headers: { Authorization: `Bearer ${token}` },
          }),
        ]);

        setDashboardStats({
          newInterestsCount: statsRes.data?.newInterestsCount ?? 0,
          totalInterestsCount: statsRes.data?.totalInterestsCount ?? 0,
          savesCount: statsRes.data?.savesCount ?? 0,
          latest5: statsRes.data?.latest5 ?? [],
        });

        setFeedCount(feedRes.data?.feed?.length ?? 0);
      } catch (err) {
        console.error("Dashboard stats error", err);
      }
    };

    fetchStats();
  }, []);

  const totalRows = dashboardStats.latest5.length;

  return (
    <DashboardLayout>
      <Box sx={{ px: { xs: 1, sm: 2 }, py: 2 }}>
        <Stack spacing={0.5} sx={{ mb: 2 }}>
          <Typography variant="h5" sx={(theme) => ({ color: makeText(theme).primary })}>
            {t("dashboard.title")}
          </Typography>

          <Typography variant="body2" sx={(theme) => ({ color: makeText(theme).secondary })}>
            {t("dashboard.subtitle")}
          </Typography>
        </Stack>

        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={12} sm={6} md={4}>
            <KPIStatCard
              label={t("dashboard.eventRequestsCount")}
              value={feedCount}
              hint={t("common.total")}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={4}>
            <KPIStatCard
              label={t("dashboard.totalInterests")}
              value={dashboardStats.totalInterestsCount}
              hint={t("common.total")}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={4}>
            <KPIStatCard label={t("dashboard.saves")} value={dashboardStats.savesCount} hint={t("common.total")} />
          </Grid>
        </Grid>

        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Card>
              <CardHeaderWhite
                title={t("dashboard.latestRequests")}
                subheader={t("dashboard.latestSummary")}
                action={
                  <Stack direction="row" spacing={1} alignItems="center">

                    <Button
                      size="small"
                      endIcon={<ArrowForwardIcon />}
                      onClick={() => navigate("/pages/profile/interests")}
                    >
                      {t("common.viewAll")}
                    </Button>

                    <IconButton size="small">
                      <MoreVertIcon fontSize="small" />
                    </IconButton>
                  </Stack>
                }
              />

              <Divider />

              <CardContent sx={{ pt: 1 }}>
                {dashboardStats.latest5.length === 0 ? (
                  <Box py={4} textAlign="center">
                    <Typography variant="body2" sx={(theme) => ({ color: makeText(theme).secondary })}>
                      {t("dashboard.noRequests")}
                    </Typography>
                  </Box>
                ) : (
                  <Box sx={{ width: "100%", overflowX: "auto" }}>
                    <Box
                      sx={{
                        display: "grid",
                        gridTemplateColumns: dashboardTableColumns,
                        columnGap: 2,
                        px: 2,
                        py: 1.75,
                        borderBottom: "1px solid rgba(255,255,255,0.2)",
                        width: "100%",
                        minWidth: 900,
                      }}
                    >
                      <Typography fontWeight={700} sx={{ ...cellSx, fontSize: "1.1rem", opacity: 0.9 }}>
                        {t("dashboard.colTitle")}
                      </Typography>
                      <Typography fontWeight={700} sx={{ ...cellSx, fontSize: "1.1rem", opacity: 0.9 }}>
                        {t("dashboard.colEmail")}
                      </Typography>
                      <Typography fontWeight={700} sx={{ ...cellSx, fontSize: "1.1rem", opacity: 0.9 }}>
                        {t("dashboard.colDate")}
                      </Typography>
                      <Typography
                        fontWeight={700}
                        sx={{ ...cellSx, textAlign: "center", fontSize: "1.1rem", opacity: 0.9 }}
                      >
                        {t("dashboard.colStatus")}
                      </Typography>
                    </Box>

                    <Box>
                      {dashboardStats.latest5.map((row) => (
                        <Box
                          key={row._id}
                          sx={{
                            display: "grid",
                            gridTemplateColumns: dashboardTableColumns,
                            columnGap: 2,
                            px: 2,
                            py: 1.75,
                            width: "100%",
                            minWidth: 900,
                            borderBottom: "1px solid rgba(255,255,255,0.12)",
                            alignItems: "center",
                            transition: "opacity 0.2s ease, background-color 0.2s ease",
                            "&:hover": {
                              backgroundColor: "rgba(255,255,255,0.05)",
                              opacity: 0.8,
                            },
                          }}
                        >
                          <Box sx={{ minWidth: 0 }}>
                            <Typography sx={cellSx}>{row.title || "-"}</Typography>
                            <Typography
                              variant="caption"
                              sx={{
                                color: "rgba(255,255,255,0.6)",
                                display: "block",
                                mt: 0.25,
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                              }}
                            >
                              {row.telephone || "-"}
                            </Typography>
                          </Box>

                          <Typography sx={cellSx}>{row.userEmail || "-"}</Typography>

                          <Typography sx={cellSx}>{formatDate(row.createdAt)}</Typography>

                          <Box sx={{ display: "flex", justifyContent: "center", minWidth: 0 }}>
                            <Chip
                              label={getStatusLabel(row.status, t)}
                              color={getStatusColor(row.status)}
                              size="small"
                            />
                          </Box>
                        </Box>
                      ))}
                    </Box>
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12}>
            <Card>
              <CardHeaderWhite
                title={t("dashboard.quickActions")}
                subheader={t("dashboard.quickActionsSubtitle")}
              />
              <Divider />
              <CardContent>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <Card variant="outlined">
                      <CardHeaderWhite title={t("dashboard.updateProfile")} subheader={t("dashboard.updateProfileDesc")} />
                      <CardContent>
                        <Button fullWidth variant="contained" onClick={() => navigate("/pages/profile/info")}>
                          {t("dashboard.editProfile")}
                        </Button>
                      </CardContent>
                    </Card>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Card variant="outlined">
                      <CardHeaderWhite
                        title={t("dashboard.refreshGallery")}
                        subheader={t("dashboard.refreshGalleryDesc")}
                      />
                      <CardContent>
                        <Button fullWidth variant="contained" onClick={() => navigate("/pages/profile/gallery")}>
                          {t("dashboard.manageGallery")}
                        </Button>
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </DashboardLayout>
  );
}